<template>
  <HomeWrapperDark :class="{ hide: !rewardTokensData?.length }" class="season-stats">
    <h3 class="title">
      {{ $t('homePageSeasonStatsTitle') }}
    </h3>
    <p class="subtitle">
      {{ $t('homePageSeasonStatsDescription') }}
    </p>
    <div class="prize-pool">
      <h3>{{ $t('coinPusherPrizePool') }}</h3>
      <div class="prize-pool-values">
        <div v-for="(token, i) of rewardTokensData" :key="token.address" class="prizeItem">
          <div class="prizeItem-icon">
            <img
              :src="tokens?.[tokenMetaData(blockchain.contracts, token.address)?.name.contract as Tokens]?.toString()"
              alt=""
              width="80"
            />
          </div>

          <span v-if="!token.hidden" class="prizeItem-value">{{
            BigNumber(prizesPool?.[i] || 0).toFormat(0, { groupSeparator: ' ', groupSize: 3 })
          }}</span>
          <span v-if="!token.hidden" class="prizeItem-value-usd"
            >$
            {{
              BigNumber(prizesPool?.[i] || 0)
                .multipliedBy(exchnageTokenRate(env)[getAddress(token.address)])
                .toFormat(0, { groupSeparator: ' ', groupSize: 3 })
            }}</span
          >
        </div>
      </div>
    </div>
    <p class="seasonEndsIn">
      {{ $t('seasonEndsIn') }}:
      <ClientOnly>
        <span>
          {{
            BigNumber(timeLeft?.days ?? 0).isGreaterThan(0)
              ? `${Formatters.text.zeroPrefix(timeLeft?.days ?? 0)}${t('coreDaysPostfix')}`
              : ''
          }}
          {{
            BigNumber(timeLeft?.hours ?? 0).isGreaterThan(0)
              ? `${Formatters.text.zeroPrefix(timeLeft?.hours ?? 0)}${t('coreHoursPostfix')}`
              : ''
          }}
          {{ `${Formatters.text.zeroPrefix(timeLeft?.minutes ?? 0)}${t('coreMinPostfix')}` }}
          {{
            BigNumber(timeLeft?.hours ?? 0).isGreaterThan(0) ||
            (BigNumber(timeLeft?.hours ?? 0).isZero() && BigNumber(timeLeft?.minutes ?? 0).isZero()) ||
            BigNumber(timeLeft?.seconds ?? 0).isZero()
              ? ''
              : `${Formatters.text.zeroPrefix(timeLeft?.seconds ?? 0)}${t('coreSecPostfix')}`
          }}
        </span>
      </ClientOnly>
    </p>
  </HomeWrapperDark>
</template>

<script lang="ts" setup>
import { BigNumber } from 'bignumber.js';
import { getAddress } from 'ethers';
import { SeasonRewardsYear1__factory } from 'public/abi/evm/types';
import useProvider from '~/composables/useProvider';
import tokens from '~/public/img/tokens';
import type { Reward } from '~/types/season';
import { apiUrls, exchnageTokenRate, Tokens } from '~/utils/constants';
import { Formatters } from '~/utils/formatters/formatters';

const { apiUrl, blockchain } = useEnvs();
const env = useRuntimeConfig().public.env;
const { getProvider } = useProvider();
const { t } = useI18n();

const currentSeasonId = inject<Ref<number>>('currentSeasonId');
const timeLeft = inject<{
  seconds: number;
  minutes: number;
  hours: number;
  days: number;
  months: number;
  isInit: boolean;
  ended: boolean;
}>('timeLeft');
const config = useRuntimeConfig();

const rewardsConfig = asyncComputed(async () => {
  const seasonYearContract = SeasonRewardsYear1__factory.connect(
    (config.public.env === 'prod' ? 41 : 1006) < (currentSeasonId?.value || 0)
      ? blockchain.contracts.SEASON_REWARDS_YEAR_2
      : blockchain.contracts.SEASON_REWARDS_YEAR_1,
    getProvider()
  );

  return seasonYearContract?.getSeasonRewardsConfig(currentSeasonId?.value || 0);
});

const { data: rewardsSeason } = useFetch<Record<string, Reward[]>>(
  () => apiUrls.seasons.positionsRewards(BigNumber(currentSeasonId?.value || 0).toNumber()),
  {
    baseURL: apiUrl
  }
);

const rewardTokensData = computed(() => {
  const items = rewardsConfig.value?.map((item) => ({
    address: getAddress(String(item[0])),
    amount: new BigNumber(String(item[1])).shiftedBy(-18),
    hidden: item[4]
  }));
  if (!items?.length) return [];
  const byAddress = items.reduce<Record<string, { amount: BigNumber; hidden: boolean }>>(
    (acc, { address, amount, hidden }) => {
      if (!acc[address]) {
        acc[address] = { amount: new BigNumber(0), hidden: false };
      }
      acc[address].amount = acc[address].amount.plus(amount);
      acc[address].hidden = acc[address].hidden || !!hidden;
      return acc;
    },
    {}
  );
  return Object.entries(byAddress).map(([address, { amount, hidden }]) => ({
    address,
    amount: amount.toString(),
    hidden
  }));
});

const prizesPool = computed(() => {
  const tokens = rewardTokensData.value;
  const entries = rewardsSeason.value ? Object.values(rewardsSeason.value).flat() : [];
  if (entries.length === 0) {
    return tokens.map((t) => Number(t.amount));
  }
  return tokens.map((token: { address: string; amount: string }) =>
    entries.filter((entry) => entry.tokenAddress === token.address).reduce((prev, cur) => prev + cur.amount, 0)
  );
});
</script>

<style lang="scss" scoped>
.season-stats {
  display: flex;
  flex-direction: column;
  width: min-content;
  padding: 20px;
  margin-bottom: 35px;

  @media screen and (max-width: 479px) {
    width: 100%;
    margin-bottom: 0;
    margin-top: 10px;
  }

  &.hide {
    height: 0;
    margin: 0;
    padding: 0;
    overflow: hidden;
  }
}

.seasonEndsIn {
  color: #88e1ff;
  font-weight: 700;
  font-size: 20px;
  text-align: center;
}

.title {
  font-weight: 700;
  font-size: 24px;
  text-align: center;
}

.subtitle {
  font-weight: 400;
  font-size: 20px;
  text-align: center;
  color: #c2c2c2;
  line-height: 1;
}

.prize-pool {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 13px 47px;
  border: 1px solid #1e353e;
  border-radius: 10px;
  margin: 20px 0;

  @media screen and (max-width: 479px) {
    width: 100%;
    padding: 13px 10px;
  }
}

.prize-pool h3 {
  font-size: 22px;

  @media screen and (max-width: 479px) {
    font-size: 18px;
  }
}

.prize-pool-values {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  gap: 100px;
  margin-top: 35px;

  @media screen and (max-width: 479px) {
    flex-wrap: wrap;
    justify-content: space-around;
    gap: 50px;
    margin-top: 35px;
    width: 100%;
  }
}

.prizeItem {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

.prizeItem-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(to bottom, #39708299 0%, #00000000 100%);
}

.prizeItem-value {
  font-weight: 500;
  font-size: 24px;
  text-align: center;
  color: #ffce1e;
  white-space: nowrap;
}

.prizeItem-value-usd {
  font-family: Eczar;
  font-weight: 500;
  font-size: 20px;
}
</style>
